import catalog from '../../../my-flask-app/assets/document-designs/catalog.json';
import type { MarginSettings } from './marginSettings';

export const PAGE_DESIGNS = catalog;
export type PageDesignStyle = keyof typeof catalog;
export type PageDesignTextKey = 'headerLeft' | 'headerRight' | 'footerLeft' | 'footerRight';
export type PageDesignText = Partial<Record<PageDesignTextKey, string>>;
export interface PageDesignSettings {
  style: 'none' | PageDesignStyle;
  showPageNumber: boolean;
  /** Keep custom wording when switching designs or temporarily disabling the design. */
  variants: Partial<Record<PageDesignStyle, PageDesignText>>;
}
export function pageDesignTextLimit(style: PageDesignStyle, key: PageDesignTextKey): number {
  return (PAGE_DESIGNS[style].limits as Partial<Record<PageDesignTextKey, number>>)[key] ?? 40;
}

export function normalizePageDesign(value: unknown): PageDesignSettings {
  const source = value && typeof value === 'object' ? value as Partial<PageDesignSettings> : {};
  const style = source.style && Object.hasOwn(PAGE_DESIGNS, source.style) ? source.style as PageDesignStyle : 'none';
  const variants: PageDesignSettings['variants'] = {};
  for (const name of Object.keys(PAGE_DESIGNS) as PageDesignStyle[]) {
    const text = source.variants?.[name];
    if (!text || typeof text !== 'object') continue;
    variants[name] = {};
    for (const key of Object.keys(PAGE_DESIGNS[name].text) as PageDesignTextKey[]) {
      if (typeof text[key] === 'string') {
        // Plain, single-line XML text; an intentionally empty field stays empty.
        variants[name]![key] = Array.from(text[key].replace(/[\p{Cc}\p{Cs}\uFFFE\uFFFF]/gu, '')).slice(0, pageDesignTextLimit(name, key)).join('');
      }
    }
  }
  return { style, showPageNumber: source.showPageNumber !== false, variants };
}

export function pageDesignText(design: PageDesignSettings): PageDesignText {
  return design.style === 'none' ? {} : { ...PAGE_DESIGNS[design.style].text, ...design.variants[design.style] };
}

export function effectiveDesignMargins<T extends Pick<MarginSettings, 'top' | 'bottom' | 'pageDesign'>>(margin: T): T {
  const { style } = normalizePageDesign(margin.pageDesign);
  if (style === 'none') return margin;
  return { ...margin, top: Math.max(margin.top, PAGE_DESIGNS[style].top), bottom: Math.max(margin.bottom, PAGE_DESIGNS[style].bottom) };
}
